import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';

const SmartSearchSelect = ({
  currentID,
  options,
  onChange,
  placeholder = 'Please select...',
  isClearable = true,
  loadingMessage = 'Searching...',
  noOptionsMessage = 'No matching results found',
  isMulti = false,
  closeMenuOnSelect,
  maxVisibleMultiTags = 2,
  allowExpandMultiTags = true,
}) => {
  const [showAllSelected, setShowAllSelected] = useState(false);

  const safeOptions = useMemo(() => {
    return Array.isArray(options) ? options : [];
  }, [options]);

  const valueMap = useMemo(() => {
    return new Map(
      safeOptions.map((option) => [
        String(option.value),
        option,
      ])
    );
  }, [safeOptions]);

  const selectOptions = useMemo(() => {
    return safeOptions.map((option) => ({
      value: String(option.value),
      label: option.label,
    }));
  }, [safeOptions]);

  const resolveSingleValue = useCallback(
    (input) => {
      if (
        input === null ||
        input === undefined ||
        input === ''
      ) {
        return null;
      }

      const rawValue =
        input && typeof input === 'object'
          ? input.value
          : input;

      if (
        rawValue === null ||
        rawValue === undefined ||
        rawValue === ''
      ) {
        return null;
      }

      const matchedOption = valueMap.get(
        String(rawValue)
      );

      if (matchedOption) {
        return {
          value: String(matchedOption.value),
          label: matchedOption.label,
        };
      }

      if (
        input &&
        typeof input === 'object' &&
        input.label
      ) {
        return {
          value: String(input.value),
          label: input.label,
        };
      }

      return null;
    },
    [valueMap]
  );

  const currentValue = useMemo(() => {
    if (isMulti) {
      if (
        !Array.isArray(currentID) ||
        currentID.length === 0
      ) {
        return [];
      }

      return currentID
        .map(resolveSingleValue)
        .filter(Boolean);
    }

    return resolveSingleValue(currentID);
  }, [
    currentID,
    isMulti,
    resolveSingleValue,
  ]);

  useEffect(() => {
    if (!isMulti) {
      setShowAllSelected(false);
      return;
    }

    const selectedCount = Array.isArray(currentValue)
      ? currentValue.length
      : 0;

    if (selectedCount <= maxVisibleMultiTags) {
      setShowAllSelected(false);
    }
  }, [
    currentValue,
    isMulti,
    maxVisibleMultiTags,
  ]);

  const filterOptions = useCallback(
    (inputValue) => {
      const keyword = String(inputValue || '')
        .trim()
        .toLowerCase();

      if (!keyword) {
        return selectOptions;
      }

      return selectOptions.filter((option) =>
        String(option.label || '')
          .toLowerCase()
          .includes(keyword)
      );
    },
    [selectOptions]
  );

  const loadOptions = useCallback(
    (inputValue) =>
      Promise.resolve(filterOptions(inputValue)),
    [filterOptions]
  );

  const handleChange = useCallback(
    (selected) => {
      if (isMulti) {
        if (
          !Array.isArray(selected) ||
          selected.length === 0
        ) {
          setShowAllSelected(false);
          onChange([]);
          return;
        }

        const fullDataList = selected.map(
          (item) => {
            return (
              valueMap.get(String(item.value)) ||
              item
            );
          }
        );

        onChange(fullDataList);
        return;
      }

      if (!selected) {
        onChange(null);
        return;
      }

      const fullData = valueMap.get(
        String(selected.value)
      );

      onChange(fullData || selected);
    },
    [
      isMulti,
      onChange,
      valueMap,
    ]
  );

  const normalizedVisibleLimit = useMemo(() => {
    if (
      Number.isInteger(maxVisibleMultiTags) &&
      maxVisibleMultiTags >= 0
    ) {
      return maxVisibleMultiTags;
    }

    return 2;
  }, [maxVisibleMultiTags]);

  const MultiValue = useCallback(
    (props) => {
      if (!isMulti || showAllSelected) {
        return <components.MultiValue {...props} />;
      }

      const selectedValues = props.getValue();

      const currentIndex = selectedValues.findIndex(
        (item) =>
          String(item.value) === String(props.data.value)
      );

      if (currentIndex < normalizedVisibleLimit) {
        return <components.MultiValue {...props} />;
      }

      return null;
    },
    [
      isMulti,
      normalizedVisibleLimit,
      showAllSelected,
    ]
  );

  const resolvedCloseMenuOnSelect =
    typeof closeMenuOnSelect === 'boolean'
      ? closeMenuOnSelect
      : !isMulti;

  const selectedCount =
    isMulti && Array.isArray(currentValue)
      ? currentValue.length
      : 0;

  const canToggleSelected =
    isMulti &&
    allowExpandMultiTags &&
    selectedCount >
    normalizedVisibleLimit;

  return (
    <div>
      <AsyncSelect
        cacheOptions
        defaultOptions={selectOptions}
        loadOptions={loadOptions}
        value={currentValue}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isMulti={isMulti}
        closeMenuOnSelect={resolvedCloseMenuOnSelect}
        blurInputOnSelect={false}
        hideSelectedOptions={false}
        captureMenuScroll={false}
        menuPortalTarget={
          typeof document !== 'undefined'
            ? document.body
            : null
        }
        menuPosition="fixed"
        loadingMessage={() => loadingMessage}
        noOptionsMessage={() => noOptionsMessage}
        styles={customStyles}
        components={{
          MultiValue,
        }}
      />

      {canToggleSelected && (
        <div className="d-flex justify-content-end mt-1">
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={() =>
              setShowAllSelected((current) => !current)
            }
          >
            {showAllSelected
              ? 'Show less'
              : `+${selectedCount - normalizedVisibleLimit} Show all`}
          </button>
        </div>
      )}
    </div>
  );
};

const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: '42px',
    boxShadow: 'none',
    alignItems: 'flex-start',
    '&:hover': {
      borderColor: '#2684ff',
    },
  }),

  valueContainer: (base) => ({
    ...base,
    flexWrap: 'wrap',
    maxHeight: 'none',
    overflow: 'visible',
  }),

  multiValue: (base) => ({
    ...base,
    maxWidth: '100%',
  }),

  multiValueLabel: (base) => ({
    ...base,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '320px',
  }),

  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

export default SmartSearchSelect;